// P1-5: which scan mode is this collection in, and what follows from that?
//
// ── WHY THIS EXISTS ──────────────────────────────────────────────────────────
// The requirements name two modes (spec §1.1, §1.2). Only interactive was ever
// built: the app always works against a collection's intent list, and the backend
// stamps `unexpected = TRUE` on every row created onsite, unconditionally.
//
// CompuCycle's drivers have been running blind jobs anyway, by using "Add
// Unexpected Asset" on a collection with no list. Five live collections have
// nothing but `unexpected` rows, so their report reads "34 unexpected assets
// against an expected count of 0" -- true of the data, and useless to the
// customer.
//
// A mode changes four unrelated things -- what a new row's `unexpected` flag is,
// which stat tiles mean anything, whether an intent list may be imported, and the
// shape of the exported report. Left inline, those four would be four independent
// `collection.mode === 'blind'` tests scattered across three components and a
// data layer, free to drift. They are one decision, so they live in one place.
//
// ⚠ MUST AGREE WITH CC_Backend/utils/scanMode.js. Same rule, two packages: CRA's
// ModuleScopePlugin forbids importing from outside src/, and the backend deploy
// artifact is CC_Backend only. The two test files share one case table and say
// so. If they drift, the client can render a screen for one mode while the server
// writes rows for the other.

const SCAN_MODE = {
    INTERACTIVE: 'interactive',   // an intent list exists; scans are matched against it
    BLIND: 'blind'                // no intent list; scans are simply recorded (spec §1.2)
};

const ALL_MODES = [SCAN_MODE.INTERACTIVE, SCAN_MODE.BLIND];

// Machine-readable refusal codes the server returns, alongside the existing
// DUPLICATE_SCAN and SERIAL_ON_ORDER. The client already switches on
// `result.code` rather than matching message text, and these keep that possible
// for mode refusals too. Both paths are defensive -- the UI hides the controls
// that would produce them -- but a stale tab can still reach either.
const BLIND_COLLECTION = 'BLIND_COLLECTION';   // an intent list was posted to a blind order
const MODE_LOCKED = 'MODE_LOCKED';             // mode change attempted on a non-empty order

// ── READING vs WRITING ───────────────────────────────────────────────────────
//
// These two look like the same question and are not, which is why both exist.
//
// `isValidMode` is for WRITES: a value arriving from a request body. It refuses
// anything unrecognised, because coercing a typo would silently create an
// interactive collection and nobody would learn about it until the report came
// out wrong.
//
// `normalizeMode` is for READS: a row written before this column existed, a
// cached collection payload from an older build, a redux slice mid-refresh. There
// is no one to report an error to at that point, and the honest answer is the one
// every such row actually is -- interactive. Migration 004 gives the column a NOT
// NULL default, so in practice this only ever fires on stale client-side state,
// but the P1-7 offline cache will make that case routine rather than theoretical.
const isValidMode = (value) => typeof value === 'string' && ALL_MODES.includes(value);

const normalizeMode = (value) => (isValidMode(value) ? value : SCAN_MODE.INTERACTIVE);

const modeOf = (collection) => normalizeMode(collection?.mode);

const isBlind = (collection) => modeOf(collection) === SCAN_MODE.BLIND;

// ── WHAT FOLLOWS FROM THE MODE ───────────────────────────────────────────────

// The `unexpected` flag for a NEWLY CREATED row. "Unexpected" means "scanned but
// not on this order's intent list" -- in a blind collection there is no list, so
// nothing can be unexpected and the flag is false for everything.
//
// ⚠ The SERVER is the one that applies this (data/assets.js -> insertAsset reads
// the collection's mode inside the INSERT). It is exported here so the frontend
// can predict the row it is about to get back, NOT so the frontend can send it: a
// client able to post `unexpected: false` onto an interactive order could hide a
// discrepancy from the customer's report. This also keeps P1-7 simple -- a queued
// offline scan carries no flag, so replaying it needs no extra field and the sync
// contract does not change.
const unexpectedFlagFor = (mode) => normalizeMode(mode) !== SCAN_MODE.BLIND;

// Importing an intent list into a blind collection would make it a hybrid: rows
// that are on a list, in an order whose report says there is no list. Refused on
// both sides -- the button is hidden, and createAssetsForCollection returns a 400
// for anyone who reaches the route directly.
const canImportIntentList = (mode) => normalizeMode(mode) !== SCAN_MODE.BLIND;

// ── DID THIS ROW COME FROM A SCAN, OR FROM THE INTENT LIST? ──────────────────
//
// The read-side companion to unexpectedFlagFor(), and the answer to two questions
// that used to be asked separately and got different answers:
//
//   * removing the row from the collection -- does that un-scan it, or delete it?
//   * may the operator who created it delete it?
//
// An intent-list row exists whether or not anyone scans it, so un-collecting it
// is meaningful: it goes back to Unscanned, still on the list, still expected. A
// row that exists ONLY because somebody scanned it has nowhere to go back TO --
// un-collecting it leaves a row representing nothing, which is what CompuCycle
// reported on 2026-08-21: remove an item in a blind order and the count drops but
// the row stays. Worse in an interactive order, where an uncollected *unexpected*
// row still counts in the Unexpected tile but matches none of the report's three
// body sections, so it vanishes from the export while remaining on screen.
//
// ⚠ This REPLACES a bare `unexpected === true` test that was standing in for this
// question at the delete-icon call site. That proxy was always approximate and
// P1-5 broke it outright: a blind row is `unexpected = false` by definition, so
// every row in a blind order read as "not added on site" and Collectors lost the
// delete icon entirely.
const wasAddedOnSite = (asset, mode) => {
    if (asset == null) return false;
    // No intent list exists, so nothing in the order can have come from one.
    if (normalizeMode(mode) === SCAN_MODE.BLIND) return true;
    return asset.unexpected === true;
};

// Whether "Remove From Collection" deletes the row rather than un-collecting it.
// The same question as wasAddedOnSite, named for the decision it drives so the
// call site reads as what it does. Kept as an alias rather than a second rule --
// if these two ever disagreed, one of them would be wrong.
const removeDeletesRow = wasAddedOnSite;

// Mode is chosen when the collection is created and fixed as soon as it holds
// anything. Flipping a scanned order re-reads every existing row's meaning and
// changes the shape of a report the client may already have seen; flipping an
// empty one costs nothing and saves deleting and recreating a mis-created job.
//
// `assetCount` is the count of NON-DELETED rows -- a soft-deleted row is not part
// of any count (see utils/stats.js) and must not lock the mode either.
const canChangeMode = (collection, assetCount) => Number(assetCount) === 0;

// ── WHAT THE SCREEN SHOWS ────────────────────────────────────────────────────
//
// Returned as an ordered list of keys rather than booleans so the tiles cannot
// get out of order between AssetManagement and CollectionManagement, and so a
// future mode has exactly one place to declare its header.
//
// Blind drops expected/matched/unscanned (all structurally zero -- there is no
// list to be expected against) and unexpected (nothing can be). What is left is
// spec §4.3's "total assets scanned", plus the duplicate count, which spec §2.1
// puts under General Requirements and therefore applies to both modes.
//
// The per-type breakdown §4.3 also requires is NOT a tile: there are fourteen
// types and five tiles already overflowed a 1366px driver laptop (P1-8). It sits
// behind a click on the Total Scanned tile, the same way the duplicate log sits
// behind the Duplicate tile.
const STAT_TILES = {
    [SCAN_MODE.INTERACTIVE]: ['expected', 'matched', 'unscanned', 'unexpected', 'duplicate'],
    [SCAN_MODE.BLIND]: ['totalScanned', 'duplicate']
};

const visibleStatTiles = (mode) => STAT_TILES[normalizeMode(mode)];

const showsStatTile = (mode, tile) => visibleStatTiles(mode).includes(tile);

// ── THE REPORT HEADER ────────────────────────────────────────────────────────
//
// CollectionReports.js writes the header by absolute cell address (D4, D5 …) and
// then hand-counts where the body starts. Every section's mergeCells offset is
// derived from that number, so getting it wrong merges the wrong row and the
// section title lands somewhere else entirely.
//
// Blind mode emits fewer header rows, so the count has to change -- and that
// arithmetic is exactly what the still-outstanding "open an XLS export in Excel"
// check exists to verify. Deriving it here, from the rows themselves, means it
// cannot be derived wrongly: `lineCount` is a function of the rows returned, not a
// constant that has to be remembered when the rows change.
//
// P1-13 is the first change to actually exercise that. It adds an interactive row
// (see below), which moves the interactive body start from `typeCount + 8` to
// `typeCount + 9` with no arithmetic edited anywhere -- which is the whole point,
// and also why the pending Excel check has to happen AFTER P1-13 rather than
// before it.
//
// Row 3 (collection date / lot / the "(Serialized / Non-Serialized Assets)"
// caption) is fixed in both modes and is not described here. These are the
// label/value pairs from row 4 down, in order.
//
//   statKey - which figure from `stats` fills the value cell
//   format  - 'serialized' renders as "(n / 0)", 'plain' as the bare number
//
// The interactive rows come from spec §4.2, which scopes them to interactive
// scans. "Total Assets Collected" and the per-type block come from §4.3, which
// requires them in BOTH modes -- so blind keeps those and drops the rest.
//
// ⚠ P1-13 ADDED "Total Unexpected Assets", and the omission is worth recording
// rather than quietly closing. §4.2 requires three counts -- matched, unexpected,
// uncollected -- and only two were here. The unexpected figure did appear in the
// export, but only inside a body-section TITLE ("ASSETS COLLECTED: Not On
// Serialized Inventory = 3"), and that whole section is skipped when the count is
// zero. So an order with nothing unexpected -- the good outcome, and the common
// one -- reported no unexpected figure at all, and a reader could not tell "none"
// from "not measured". A required count has to be a count, present at zero.
//
// It sits with the other two §4.2 figures rather than at the end, so §4.3's
// "Total Assets Collected" still closes the block.
//
// `format: 'plain'` deliberately: the "(n / 0)" shape means "n serialized, 0
// non-serialized" and belongs to the three On-Inventory rows. An unexpected asset
// was never on the inventory, so the split has nothing to say about it.
//
// ⚠ This figure is `stats.unexpected`, which counts EVERY row not on the intent
// list -- collected or not. The report's body has three sections and all three
// filter on collected, so an unexpected row that is *not* collected is counted here
// and listed nowhere. That is a real reachable state: "Add Unexpected Asset" writes
// `collected: false`, and after P1-6 a Manager is exactly who still has that button.
//
// Counted anyway, on purpose. §4.2 asks for "total unexpected assets", and the
// honest total is the one the tile shows; suppressing the difference would make the
// report disagree with the screen. The missing fourth body section is a known
// residual from P1-2, recorded in REMEDIATION_PROGRESS.md against P1-3/P1-6 -- it is
// the body that is short, not this count that is wrong.
const INTERACTIVE_HEADER_ROWS = [
    { label: 'Total Assets On Inventory:', statKey: 'expected', format: 'serialized' },
    { label: 'Assets Collected On Inventory:', statKey: 'matched', format: 'serialized' },
    { label: 'Assets Not Collected On Inventory:', statKey: 'unscanned', format: 'serialized' },
    { label: 'Total Unexpected Assets:', statKey: 'unexpected', format: 'plain' },
    { label: 'Total Assets Collected:', statKey: 'totalScanned', format: 'plain' }
];

const BLIND_HEADER_ROWS = [
    { label: 'Total Assets Scanned:', statKey: 'totalScanned', format: 'plain' }
];

// The left-hand block (Customer / contact / address) occupies A4:B7 in both
// modes, so the body can never start above row 8 however short the right-hand
// column gets. Without this floor a blind report with no asset types at all
// would compute a body start of 5 and overwrite the address.
const MIN_BODY_START = 8;

/**
 * Where does the report header end?
 *
 *   mode      - the collection's scan mode
 *   typeCount - how many per-type lines follow the fixed rows (P1-9 makes this
 *               the fourteen supported types plus any retired type that actually
 *               has a count, so it is 14 in the normal case)
 *
 * Returns { rows, firstStatRow, firstTypeRow, lineCount }, where `lineCount` is
 * the value CollectionReports.js carries into the body sections.
 */
const reportHeaderRows = ({ mode, typeCount = 0 } = {}) => {
    const rows = normalizeMode(mode) === SCAN_MODE.BLIND
        ? BLIND_HEADER_ROWS
        : INTERACTIVE_HEADER_ROWS;

    const firstStatRow = 4;                        // row 3 is the date/lot caption line
    const firstTypeRow = firstStatRow + rows.length;
    const types = Number.isFinite(typeCount) && typeCount > 0 ? Math.trunc(typeCount) : 0;

    return {
        rows,
        firstStatRow,
        firstTypeRow,
        lineCount: Math.max(MIN_BODY_START, firstTypeRow + types)
    };
};

// The label on the report's first body section. In interactive mode it
// distinguishes rows that were on the serialized inventory from rows that were
// not; in blind mode there is no inventory to be on, and calling a blind scan
// "On Serialized Inventory" would assert something untrue about a list that does
// not exist. The other two sections are empty in blind mode by construction, so
// they need no equivalent.
const collectedSectionLabel = (mode) => (normalizeMode(mode) === SCAN_MODE.BLIND
    ? 'ASSETS SCANNED'
    : 'ASSETS COLLECTED: On Serialized Inventory');

// For the mode selector in the New/Edit Collection dialogs. Kept next to the
// values so a new mode cannot be added without a label, following the
// ENTRY_METHOD_LABELS precedent in scanInput.js.
const SCAN_MODE_LABELS = {
    [SCAN_MODE.INTERACTIVE]: 'Interactive (intent list)',
    [SCAN_MODE.BLIND]: 'Blind (no intent list)'
};

const describeScanMode = (mode) => SCAN_MODE_LABELS[normalizeMode(mode)];

const exports = {
    SCAN_MODE,
    ALL_MODES,
    BLIND_COLLECTION,
    MODE_LOCKED,
    SCAN_MODE_LABELS,
    isValidMode,
    normalizeMode,
    modeOf,
    isBlind,
    unexpectedFlagFor,
    canImportIntentList,
    wasAddedOnSite,
    removeDeletesRow,
    canChangeMode,
    visibleStatTiles,
    showsStatTile,
    reportHeaderRows,
    collectedSectionLabel,
    describeScanMode,
};

export default exports;
export {
    SCAN_MODE,
    ALL_MODES,
    BLIND_COLLECTION,
    MODE_LOCKED,
    SCAN_MODE_LABELS,
    isValidMode,
    normalizeMode,
    modeOf,
    isBlind,
    unexpectedFlagFor,
    canImportIntentList,
    wasAddedOnSite,
    removeDeletesRow,
    canChangeMode,
    visibleStatTiles,
    showsStatTile,
    reportHeaderRows,
    collectedSectionLabel,
    describeScanMode,
};
